import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Wand2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import VoiceCloneCard from "@/components/VoiceCloneCard";
import CaptionEditor from "@/components/CaptionEditor";
import FounderVoiceRecorder from "@/components/FounderVoiceRecorder";
import FounderVoiceDraft from "@/components/FounderVoiceDraft";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DRAFT_SCRIPT =
  "I'm Uncle Danny. I built Umbra because the hardest things I've carried were the ones I couldn't write down. After Baghdad, a blank page felt like one more place to be alone with it. What helped was being heard — a wife who listened, veteran programs, a lot of hard, out-loud work. So Umbra lets you speak the answer and hear it back. Start with Chapter One. Say it out loud.";
const btn = "rounded-none uppercase tracking-wider text-xs";

export default function FounderVoiceAdmin() {
  const [voice, setVoice] = useState(null);
  const [script, setScript] = useState(DRAFT_SCRIPT);
  const [voiceId, setVoiceId] = useState("");
  const [busy, setBusy] = useState("");
  const [hasClone, setHasClone] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/site/founder-voice").then((r) => setVoice(r.data));
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    load();
    api.get("/admin/founder-voice/draft").then((r) => setDraft(r.data)).catch(() => {});
  }, []);

  const run = async (kind, fn) => {
    setBusy(kind);
    try {
      setVoice((await fn()).data);
      toast.success(kind === "delete" ? "Voice note removed." : "Voice note is live on /about.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong.");
    } finally {
      setBusy("");
    }
  };

  const upload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append("audio", f);
    run("upload", () => api.post("/admin/founder-voice/upload", form, { headers: { "Content-Type": "multipart/form-data" } }));
    e.target.value = "";
  };
  const generate = async () => {
    setBusy("generate");
    try {
      setDraft((await api.post("/admin/founder-voice/generate", { script, voice_id: voiceId || null })).data);
      toast.success("Draft ready — listen, then publish.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong.");
    } finally {
      setBusy("");
    }
  };
  const remove = () => run("delete", () => api.delete("/admin/founder-voice"));

  return (
    <section className="border border-obsidian-600 p-6 mb-10" data-testid="founder-voice-admin">
      <h2 className="font-serif text-2xl text-bone-100 mb-2">Founder voice note (/about)</h2>
      <p className="font-sans text-sm text-bone-400 mb-5">Hidden on /about until a clip exists. Record it right here, upload a file, or generate one from your ElevenLabs voice clone.</p>
      <FounderVoiceDraft draft={draft} onPublished={(v) => { setVoice(v); setDraft(null); }} onDiscarded={() => setDraft(null)} />
      {voice?.available && (
        <div className="mb-6 flex items-center gap-4 flex-wrap" data-testid="founder-voice-current">
          <audio controls src={`${BACKEND}${voice.audio_url}`} className="max-w-full" />
          <span className="font-sans text-xs uppercase tracking-[0.2em] text-bone-500">{voice.source === "clone" ? "Generated" : "Uploaded"}</span>
          <Button onClick={remove} disabled={!!busy} data-testid="founder-voice-delete" className={`${btn} bg-transparent border border-obsidian-600 text-bone-400 hover:text-terracotta hover:bg-transparent`}>
            <Trash2 className="h-4 w-4 mr-2" /> Remove
          </Button>
        </div>
      )}
      {voice?.available && <CaptionEditor key={`${voice.updated_at}:${JSON.stringify(voice.captions || [])}`} captions={voice.captions || []} onSaved={setVoice} />}
      <FounderVoiceRecorder onSaved={setVoice} />
      <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={upload} data-testid="founder-voice-file" />
      <Button onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="founder-voice-upload-btn" className={`${btn} bg-transparent border border-bone-500 text-bone-100 hover:border-terracotta hover:text-terracotta hover:bg-transparent mb-8`}>
        {busy === "upload" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />} Upload recording
      </Button>
      <div className="mb-6" data-testid="founder-voice-clone-setup">
        <p className="font-sans text-xs uppercase tracking-[0.2em] text-terracotta mb-3">
          Your voice clone · {hasClone ? "ready" : "record or upload a 30–90s sample to create it"}
        </p>
        <VoiceCloneCard onChange={(s) => setHasClone(!!s?.has_clone)} />
      </div>
      <p className="font-sans text-xs uppercase tracking-[0.2em] text-terracotta mb-2">Or generate from your clone · draft script (edit before generating)</p>
      <Textarea value={script} onChange={(e) => setScript(e.target.value)} maxLength={900} data-testid="founder-voice-script" className="min-h-[140px] bg-obsidian-800 border-obsidian-600 rounded-none text-bone-100 mb-2" />
      <p className="font-sans text-xs text-bone-500 mb-3">{script.trim().split(/\s+/).length} words · about {Math.round(script.trim().split(/\s+/).length / 2.5)}s spoken</p>
      <Input value={voiceId} onChange={(e) => setVoiceId(e.target.value)} placeholder="ElevenLabs voice ID (optional — defaults to your account's clone)" data-testid="founder-voice-id" className="bg-obsidian-800 border-obsidian-600 rounded-none text-bone-100 mb-3" />
      <Button onClick={generate} disabled={!!busy || script.trim().length < 10} data-testid="founder-voice-generate-btn" className={`${btn} bg-terracotta hover:bg-terracotta-deep text-obsidian-900`}>
        {busy === "generate" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />} Generate draft with my voice
      </Button>
    </section>
  );
}
